import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { CircularProgressFullScreen } from '../../components/CircularProgressFullScreen/CircularProgressFullScreen';
import { hasPersist } from '../../store/_core/hasPersist';
import useParameterStore from '../../store/parameterStore/useParameterStore';
import useRouteStore from '../../store/routeStore/useRouteStore';
import useSignOutStore from '../../store/signOutStore/useSignOutStore';
import { AuthStatus } from '../../store/userStore/enums/AuthStatus';
import { SessionCheckTrigger } from '../../store/userStore/enums/SessionCheckTrigger';
import useUserStore from '../../store/userStore/useUserStore';

const SessionGuard = ({ children }: { children: ReactNode }): ReactNode => {
  const location = useLocation();

  const {
    authStatus,
    userSignedUp,
    bootstrapSession,
    computed: { isAuthenticated },
  } = useUserStore();

  const { isSigningOut } = useSignOutStore();

  const {
    authRedirectPath,
    isAuthenticatedRoute,
    setAuthRedirectPath,
    computed: { nonAuthRedirectPath },
  } = useRouteStore();

  const [fetchingUserSession, setFetchingUserSession] = useState(
    (): boolean => authStatus === AuthStatus.PENDING,
  );

  const authenticatedRoute = useMemo((): boolean => {
    return isAuthenticatedRoute(location.pathname);
  }, [location.pathname, isAuthenticatedRoute]);

  useEffect((): void => {
    // This is used to redirect the user back to the route they were trying to reach.
    // store the current path of the authenticated route for redirection after sign-in or sign-up.
    if (authenticatedRoute) {
      setAuthRedirectPath(location.pathname);
    }
  }, [authenticatedRoute, location.pathname, setAuthRedirectPath]);

  useEffect((): void => {
    // rehydrate parameter store for partialization
    // based on session status
    if (hasPersist(useParameterStore)) {
      useParameterStore.persist.rehydrate();
    }

    // prevent fetching of current user if:
    // - user is already defined in user store (user is signed in)
    // - fetching current user has already failed (to avoid repeated requests)
    if (authStatus !== AuthStatus.PENDING) {
      setFetchingUserSession(false);
      return;
    }

    setFetchingUserSession(true);

    void bootstrapSession(SessionCheckTrigger.SESSION_GUARD).finally((): void => {
      setFetchingUserSession(false);
    });
  }, [authStatus, bootstrapSession]);

  /**
   * while fetching the current user, display a spinner
   */
  if (fetchingUserSession) {
    return <CircularProgressFullScreen />;
  }

  /**
   * local environment redirect bypass
   * - allows routes to be reached regardless of user & token state
   */
  // if (import.meta.env.VITE_APP_ENV === 'local') {
  //   return children;
  // }

  /**
   * redirect handling (sign-out)
   * where:
   * - user is in the process of signing out
   */
  if (isSigningOut) {
    return <Navigate to="/sign-out" />;
  }

  /**
   * redirect handling (sign-in)
   * where:
   * - the fetched user is not defined / user signed out
   * - user is attempting to reach an authenticated route
   */
  if (!isAuthenticated && authenticatedRoute) {
    return <Navigate to={nonAuthRedirectPath} />;
  }

  /**
   * redirect handling (dashboard)
   * where:
   * - the fetched user is defined / user is signed in
   * - user is attempting to reach a non-authenticated route
   * - user did not just sign up (to avoid redirect loop)
   */
  if (isAuthenticated && !authenticatedRoute && !userSignedUp) {
    return <Navigate to={authRedirectPath} />;
  }

  // valid route requested, load the child component
  return children;
};

export default SessionGuard;
